<script setup lang="ts">
import PreviewHeader from "@/components/Every24HoursReportPreviewParts/PreviewHeader.vue";
import PreviewCharts from "@/components/Every24HoursReportPreviewParts/PreviewCharts.vue";
import {
  DEFAULT_OUTPUT_TIME,
  DEFAULT_DRIVING_DISTANCE,
  DEFAULT_DRIVING_TIME
} from "@/services/consts/every_24_hours_report";
import { formatDateTimeWithMilliseconds } from '@/services/functions';
import every24HoursPreviewType from "@/types/responses/every24HoursPreview";
import { Ref, ref, onMounted } from "vue";
import moment from 'moment';

const emit = defineEmits(['changeDetailData']);
const props = defineProps<{
  deliveryPlanRecords: every24HoursPreviewType[]
  downloading: boolean
  isShowableDownloadButton: boolean
}>();

const selectedOutputTime: Ref<string[]> = ref([]);
const chartDetails: Ref<{
  deliveryPlanDate: string,
  displayStartTime: string,
  drivingDistance: string,
  drivingTime: string,
}[]> = ref([]);
const defaultChartDetail = {
  deliveryPlanDate: '',
  displayStartTime: '',
  drivingDistance: DEFAULT_DRIVING_DISTANCE,
  drivingTime: DEFAULT_DRIVING_TIME,
};

onMounted(() => {
  for (let i = 0; i < props.deliveryPlanRecords.length; i++) {
    selectedOutputTime.value.push(DEFAULT_OUTPUT_TIME);
    chartDetails.value.push({ ...defaultChartDetail });
  }
});

function changeChartDetail(values: {
    index: number,
    displayStartTime: number,
    drivingDistance: string,
    drivingTime: string
    xLabel: string[]
    speed: number[][]
    distance: number[][]
    rotationSpeed: number[][]
  }
) {
  const deliveryPlanDate = moment(values.displayStartTime).format("YYYY-MM-DD");
  const displayStartTime = moment(values.displayStartTime).format("HH:mm:ss");
  chartDetails.value[values.index] = {
    deliveryPlanDate: deliveryPlanDate,
    displayStartTime: displayStartTime,
    drivingDistance: values.drivingDistance,
    drivingTime: values.drivingTime
  }

  emit('changeDetailData', {
    delivery_plan_id: props.deliveryPlanRecords[values.index].delivery_plan_id,
    output_time: selectedOutputTime.value[values.index],
    from_date_time: props.deliveryPlanRecords[values.index].leaving_warehouse,
    to_date_time: props.deliveryPlanRecords[values.index].returning_warehouse,
    date_at_output_time: deliveryPlanDate,
    start_time_at_output_time: displayStartTime,
    distance_at_output_time: values.drivingDistance,
    driving_time_at_output_time: values.drivingTime,
    x_label: values.xLabel.join(','),
    distance_data: makeEmitData(values.distance),
  });
}

function makeEmitData(data: number[][]) {
  const result: {[key: string]: number} = {};
  data.forEach((item: number[]) => {
    const dateTime = formatDateTimeWithMilliseconds(new Date(item[0]))
    result[dateTime] = item[1];
  });

  return result;
}
</script>
<template>
  <div class="position-relative">
    <div
      v-for="(deliveryPlan, index) in props.deliveryPlanRecords"
      :key="index"
      class="w-100 text-center mt-5 every-24hours-report-container"
    >
      <PreviewHeader
        v-model="selectedOutputTime[index]"
        :index="index"
        :deliveryPlan="deliveryPlan"
        :downloading="props.downloading"
        :chartDetails="chartDetails[index]"
      />
      <PreviewCharts
        :index="index"
        :deliveryPlan="deliveryPlan"
        :outPutTime="selectedOutputTime[index]"
        @changeDetailData="changeChartDetail"
      />
    </div>
  </div>
</template>
<style scoped>
.driving-record-table * {
  background-color: rgb(241, 236, 236);
  padding: 20px 0px 20px 0px;
}
.data-for-each-section {
  background-color: gray;
}

.output-time-input-area {
  background-color: rgb(241, 236, 236);
}

.v-btn--disabled.v-btn--variant-contained {
  color:inherit;
  box-shadow: inherit;
  background: rgb(21, 130, 156) !important;
}

.padding-top-2px {
  padding-top: 2px;
}

.input-range[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  outline: none;
  height: 14px;
  width: 100%;
  background: #C0C0C0;
  border-radius: 10px;
  border: solid 3px #EEEEEE;
}

.input-range[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  background: #53aeff;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
}
</style>
